import {
  concretizePath,
  parseInstanceSuffix,
  setValueAtPath,
  type BlockNode,
  type DocumentV3,
  type PageV3,
  type RepeatFrame,
  type StyleOverrides,
} from '@docgen/schema';

/**
 * Pure, immutable operations on a v3 document tree. Every function returns a
 * new document; untouched branches keep referential identity where cheap.
 */

export function newBlockId(type: string): string {
  return `${type}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Deep-clone a block subtree with fresh ids (for duplicate / template insert). */
export function cloneWithNewIds(node: BlockNode): BlockNode {
  return {
    ...node,
    id: newBlockId(node.type),
    props: structuredClone(node.props),
    ...(node.style ? { style: { ...node.style } } : {}),
    ...(node.children ? { children: node.children.map(cloneWithNewIds) } : {}),
  };
}

export interface FoundBlock {
  block: BlockNode;
  pageId: string;
  parentBlockId: string | null;
  index: number;
}

function findInList(
  list: BlockNode[],
  blockId: string,
  pageId: string,
  parentBlockId: string | null,
): FoundBlock | undefined {
  for (let i = 0; i < list.length; i++) {
    const node = list[i]!;
    if (node.id === blockId) return { block: node, pageId, parentBlockId, index: i };
    if (node.children) {
      const hit = findInList(node.children, blockId, pageId, node.id);
      if (hit) return hit;
    }
  }
  return undefined;
}

export function findBlock(doc: DocumentV3, blockId: string): FoundBlock | undefined {
  for (const page of doc.pages) {
    const hit = findInList(page.blocks, blockId, page.id, null);
    if (hit) return hit;
  }
  return undefined;
}

function mapList(list: BlockNode[], fn: (b: BlockNode) => BlockNode | null): BlockNode[] {
  const out: BlockNode[] = [];
  for (const node of list) {
    const mapped = fn(node);
    if (mapped === null) continue;
    out.push(
      mapped.children ? { ...mapped, children: mapList(mapped.children, fn) } : mapped,
    );
  }
  return out;
}

function mapPages(doc: DocumentV3, fn: (b: BlockNode) => BlockNode | null): DocumentV3 {
  return { ...doc, pages: doc.pages.map((p) => ({ ...p, blocks: mapList(p.blocks, fn) })) };
}

export function updateBlock(
  doc: DocumentV3,
  blockId: string,
  fn: (b: BlockNode) => BlockNode,
): DocumentV3 {
  return mapPages(doc, (b) => (b.id === blockId ? fn(b) : b));
}

export function setBlockProp(doc: DocumentV3, blockId: string, prop: string, value: unknown): DocumentV3 {
  return updateBlock(doc, blockId, (b) => ({ ...b, props: { ...b.props, [prop]: value } }));
}

export function setBlockStyle(
  doc: DocumentV3,
  blockId: string,
  patch: Partial<StyleOverrides>,
): DocumentV3 {
  return updateBlock(doc, blockId, (b) => {
    const style: StyleOverrides = { ...b.style };
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) delete (style as Record<string, unknown>)[k];
      else (style as Record<string, unknown>)[k] = v;
    }
    return Object.keys(style).length ? { ...b, style } : { ...b, style: undefined };
  });
}

export function removeBlock(doc: DocumentV3, blockId: string): DocumentV3 {
  return mapPages(doc, (b) => (b.id === blockId ? null : b));
}

export function duplicateBlock(doc: DocumentV3, blockId: string): DocumentV3 {
  const found = findBlock(doc, blockId);
  if (!found) return doc;
  const copy = cloneWithNewIds(found.block);
  return insertBlock(doc, found.pageId, found.parentBlockId, found.index + 1, copy);
}

export function insertBlock(
  doc: DocumentV3,
  pageId: string,
  parentBlockId: string | null,
  index: number,
  node: BlockNode,
): DocumentV3 {
  const insertAt = (list: BlockNode[]): BlockNode[] => {
    const i = Math.max(0, Math.min(index, list.length));
    return [...list.slice(0, i), node, ...list.slice(i)];
  };
  return {
    ...doc,
    pages: doc.pages.map((page) => {
      if (page.id !== pageId) return page;
      if (parentBlockId === null) return { ...page, blocks: insertAt(page.blocks) };
      return {
        ...page,
        blocks: mapList(page.blocks, (b) =>
          b.id === parentBlockId ? { ...b, children: insertAt(b.children ?? []) } : b,
        ),
      };
    }),
  };
}

function isDescendant(node: BlockNode, blockId: string): boolean {
  if (node.id === blockId) return true;
  return (node.children ?? []).some((c) => isDescendant(c, blockId));
}

/** Move a block to a new location; no-op if the target is inside the block. */
export function moveBlock(
  doc: DocumentV3,
  blockId: string,
  pageId: string,
  parentBlockId: string | null,
  index: number,
): DocumentV3 {
  const found = findBlock(doc, blockId);
  if (!found) return doc;
  if (parentBlockId !== null && isDescendant(found.block, parentBlockId)) return doc;
  // removing before inserting shifts the index when staying in the same list
  const sameList = found.pageId === pageId && found.parentBlockId === parentBlockId;
  const targetIndex = sameList && found.index < index ? index - 1 : index;
  const without = removeBlock(doc, blockId);
  return insertBlock(without, pageId, parentBlockId, targetIndex, found.block);
}

/* --------------------- binding → data write-through --------------------- */

export interface BlockChain {
  page: PageV3;
  /** ancestor blocks from page level down to (and including) the block */
  chain: BlockNode[];
}

/** Page + ancestor chain (page level → the block itself) for breadcrumbs. */
export function blockAncestry(doc: DocumentV3, blockId: string): BlockChain | undefined {
  return findChain(doc, blockId);
}

function findChain(doc: DocumentV3, blockId: string): BlockChain | undefined {
  const walk = (list: BlockNode[], acc: BlockNode[]): BlockNode[] | undefined => {
    for (const node of list) {
      if (node.id === blockId) return [...acc, node];
      if (node.children) {
        const hit = walk(node.children, [...acc, node]);
        if (hit) return hit;
      }
    }
    return undefined;
  };
  for (const page of doc.pages) {
    const chain = walk(page.blocks, []);
    if (chain) return { page, chain };
  }
  return undefined;
}

/**
 * The active repeat frames for one rendered instance of a block, outermost
 * first: the page's repeat, then each ancestor's (and the block's own).
 * `instanceSuffix` (":0:2" from a rendered key) supplies the indices; missing
 * indices default to 0.
 */
export function repeatFramesFor(
  doc: DocumentV3,
  blockId: string,
  instanceSuffix = '',
): RepeatFrame[] {
  const found = findChain(doc, blockId);
  if (!found) return [];
  const repeats: { over: string; as: string }[] = [];
  if (found.page.repeat) repeats.push(found.page.repeat);
  for (const node of found.chain) {
    if (node.repeat) repeats.push(node.repeat);
  }
  const indices = parseInstanceSuffix(instanceSuffix);
  return repeats.map((r, i) => ({ over: r.over, as: r.as, index: indices[i] ?? 0 }));
}

/** Rewrite a block's bind path to the concrete data path for one instance. */
export function concreteBindPath(
  doc: DocumentV3,
  blockId: string,
  bindPath: string,
  instanceSuffix = '',
): string {
  return concretizePath(bindPath, repeatFramesFor(doc, blockId, instanceSuffix));
}

/** Set a value in document data at a concrete dot path. */
export function setDocData(doc: DocumentV3, path: string, value: unknown): DocumentV3 {
  return { ...doc, data: setValueAtPath(doc.data, path, value) };
}

/** Where hand edits to bound text are remembered (the API's `OVERRIDES_DATA_PATH`). */
export const OVERRIDES_DATA_KEY = 'overrides';

/**
 * Remembers a hand edit to bound text under `data.overrides`, keyed by its data
 * path. The server re-applies overrides after it regenerates pricing, program
 * names and contents on save, so the edit sticks; it ignores paths it does not
 * regenerate and drops an override once the generated text matches it.
 */
export function recordOverride(doc: DocumentV3, path: string, value: unknown): DocumentV3 {
  if (typeof value !== 'string' && typeof value !== 'number') return doc;
  const existing = doc.data[OVERRIDES_DATA_KEY];
  const overrides = typeof existing === 'object' && existing !== null && !Array.isArray(existing) ? existing : {};
  return { ...doc, data: { ...doc.data, [OVERRIDES_DATA_KEY]: { ...overrides, [path]: value } } };
}

/* ------------------------------ pages ------------------------------ */

export function newPageId(): string {
  return `page-${Math.random().toString(36).slice(2, 7)}`;
}

export function addPage(doc: DocumentV3, page: PageV3, index?: number): DocumentV3 {
  const i = index === undefined ? doc.pages.length : Math.max(0, Math.min(index, doc.pages.length));
  return { ...doc, pages: [...doc.pages.slice(0, i), page, ...doc.pages.slice(i)] };
}

export function removePage(doc: DocumentV3, pageId: string): DocumentV3 {
  return { ...doc, pages: doc.pages.filter((p) => p.id !== pageId) };
}

export function updatePage(doc: DocumentV3, pageId: string, fn: (p: PageV3) => PageV3): DocumentV3 {
  return { ...doc, pages: doc.pages.map((p) => (p.id === pageId ? fn(p) : p)) };
}

export function duplicatePage(doc: DocumentV3, pageId: string): DocumentV3 {
  const i = doc.pages.findIndex((p) => p.id === pageId);
  const page = doc.pages[i];
  if (!page) return doc;
  const copy: PageV3 = { ...page, id: newPageId(), blocks: page.blocks.map(cloneWithNewIds) };
  return addPage(doc, copy, i + 1);
}

export function movePage(doc: DocumentV3, pageId: string, index: number): DocumentV3 {
  const from = doc.pages.findIndex((p) => p.id === pageId);
  const page = doc.pages[from];
  if (!page) return doc;
  const rest = doc.pages.filter((p) => p.id !== pageId);
  const i = Math.max(0, Math.min(from < index ? index - 1 : index, rest.length));
  return { ...doc, pages: [...rest.slice(0, i), page, ...rest.slice(i)] };
}

/** The data keys the server derives from the deal on every save (intro text, footers, headings). */
export const SERVER_DERIVED_DATA_KEYS = ['intro', 'labels'] as const;

/**
 * Takes the server's freshly derived intro and labels into the local document,
 * so an edit to the client, event or intro note shows up in the pages without a
 * reload. Returns `local` itself when nothing derived changed.
 *
 * @param local - The document the editor holds.
 * @param server - The document the save returned.
 * @returns `local`, or a copy carrying the server's derived data.
 */
export function withServerDerivedData(local: DocumentV3, server: DocumentV3): DocumentV3 {
  const localData = local.data as Record<string, unknown>;
  const serverData = server.data as Record<string, unknown>;
  const changed = SERVER_DERIVED_DATA_KEYS.filter(
    (key) => serverData[key] !== undefined && JSON.stringify(serverData[key]) !== JSON.stringify(localData[key]),
  );
  if (changed.length === 0) return local;
  return { ...local, data: { ...localData, ...Object.fromEntries(changed.map((key) => [key, serverData[key]])) } };
}
